import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, RotateCcw, FileText, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: 'gallery', name: 'Our Apartments', icon: '🏠' },
  { id: 'hero', name: 'Hero Section', icon: '🌟' },
  { id: 'reviews', name: 'Customer Reviews', icon: '⭐' },
  { id: 'booking', name: 'Book Your Stay', icon: '📅' },
  { id: 'contact', name: 'Contact Section', icon: '📞' }
];

const AdminContent = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [activeSection, setActiveSection] = useState('gallery');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchContent();
  }, [navigate]);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('adminToken')}`
  });

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/settings/content`);
      setContent(response.data.content || {});
    } catch (error) {
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const saveSection = async (section) => {
    setSaving(prev => ({ ...prev, [section]: true }));
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/settings/content/${section}`,
        {
          title: content[section]?.title,
          subtitle: content[section]?.subtitle,
          description: content[section]?.description
        },
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json'
          }
        }
      );
      toast.success(`${SECTIONS.find(s => s.id === section)?.name} content saved!`);
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(prev => ({ ...prev, [section]: false }));
    }
  };

  const resetSection = async (section) => {
    if (!window.confirm('Reset this section to default content?')) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/settings/content/${section}/reset`,
        {},
        { headers: getAuthHeaders() }
      );
      setContent(prev => ({
        ...prev,
        [section]: response.data.content
      }));
      toast.success('Content reset to default');
    } catch (error) {
      toast.error('Failed to reset content');
    }
  };

  const currentSection = content[activeSection] || {};

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="outline"
              size="sm"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Dashboard
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-amber-900">Edit Website Content</h1>
              <p className="text-gray-500 text-sm">Customize section titles and descriptions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Section Tabs */}
            <div className="md:col-span-1">
              <div className="bg-white rounded-xl shadow-sm p-4 sticky top-4">
                <h3 className="font-semibold text-gray-700 mb-3">Sections</h3>
                <div className="space-y-2">
                  {SECTIONS.map(section => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                        activeSection === section.id
                          ? 'bg-amber-100 text-amber-800 font-medium'
                          : 'hover:bg-gray-100 text-gray-600'
                      }`}
                    >
                      <span className="text-xl">{section.icon}</span>
                      <span className="text-sm">{section.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Edit Form */}
            <div className="md:col-span-3">
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <span className="text-2xl">{SECTIONS.find(s => s.id === activeSection)?.icon}</span>
                    {SECTIONS.find(s => s.id === activeSection)?.name}
                  </h2>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => resetSection(activeSection)}
                      variant="outline"
                      size="sm"
                      className="text-gray-600"
                    >
                      <RotateCcw size={16} className="mr-1" />
                      Reset
                    </Button>
                    <Button
                      onClick={() => saveSection(activeSection)}
                      disabled={saving[activeSection]}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      size="sm"
                    >
                      {saving[activeSection] ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        <>
                          <Save size={16} className="mr-1" />
                          Save Changes
                        </>
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Section Title
                    </label>
                    <Input
                      value={currentSection.title || ''}
                      onChange={(e) => handleChange(activeSection, 'title', e.target.value)}
                      placeholder="Enter section title"
                      className="text-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">This is the main heading displayed on the website</p>
                  </div>

                  {/* Subtitle */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Subtitle (Optional)
                    </label>
                    <Input
                      value={currentSection.subtitle || ''}
                      onChange={(e) => handleChange(activeSection, 'subtitle', e.target.value)}
                      placeholder="Enter subtitle"
                    />
                    <p className="text-xs text-gray-500 mt-1">A short tagline below the title</p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={currentSection.description || ''}
                      onChange={(e) => handleChange(activeSection, 'description', e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      placeholder="Enter description text"
                    />
                    <p className="text-xs text-gray-500 mt-1">The paragraph text shown below the title</p>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-8 pt-6 border-t">
                  <h3 className="text-sm font-medium text-gray-700 mb-4 flex items-center gap-2">
                    <FileText size={16} />
                    Preview
                  </h3>
                  <div className="bg-amber-50 rounded-xl p-6 text-center">
                    <h2 className="text-3xl font-bold text-amber-900 mb-2">
                      {currentSection.title || 'Section Title'}
                    </h2>
                    {currentSection.subtitle && (
                      <p className="text-lg text-amber-700 mb-2">{currentSection.subtitle}</p>
                    )}
                    <p className="text-gray-600 max-w-2xl mx-auto">
                      {currentSection.description || 'Section description will appear here...'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminContent;
